import { useMemo } from "react";
import useIsNarrow from "../hooks/useIsNarrow";
import { calculateRentalAnalysis } from "../utils/rentalAnalysisCalculator";

export default function RentalValueAnalysis({
  property,
  prediction,
  propertyAnalysis,
  mlRent,
  agentRent,
  lowRent,
  highRent,
  discountedPrice,
  mortgageConfig,
  tenantRent,
  monthlyAssessment = 0,
}) {
  const narrow = useIsNarrow();
  // Calculate rental value analysis using utility function
  // Now using lowRent and highRent which respect radio button selections
  const analysis = useMemo(() => {
    return calculateRentalAnalysis({
      property,
      prediction,
      propertyAnalysis,
      mlRent,
      agentRent,
      userRent: highRent,
      discountedPrice,
      lowRent,
      highRent,
      mortgageConfig,
      monthlyAssessment,
    });
  }, [property, prediction, propertyAnalysis, mlRent, agentRent, highRent, lowRent, discountedPrice, mortgageConfig, monthlyAssessment]);

  // Calculate tenant rent analysis if tenant rent is available
  const tenantAnalysis = useMemo(() => {
    if (!tenantRent) return null;
    return calculateRentalAnalysis({
      property,
      prediction,
      propertyAnalysis,
      mlRent: tenantRent,
      agentRent: tenantRent,
      userRent: tenantRent,
      discountedPrice,
      lowRent: tenantRent,
      highRent: tenantRent,
      mortgageConfig,
      monthlyAssessment,
    });
  }, [property, prediction, propertyAnalysis, tenantRent, discountedPrice, mortgageConfig, monthlyAssessment]);

  if (!analysis) return null;

  // Get property price for break-even down payment dollar amount calculation
  const propertyPrice = discountedPrice || property?.price || property?.asking_price || 0;

  // Styles
  const containerStyle = {
    backgroundColor: "white",
    padding: narrow ? "16px 12px" : "20px",
    borderRadius: "8px",
    border: "2px solid #000",
    marginBottom: "20px",
  };

  // Horizontal scroll for the wide table, contained so the card's border stays put.
  const scrollWrapStyle = {
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "16px",
    color: "#333",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: narrow ? "13px" : "14px",
    // Six columns of currency cannot squash into a phone; below this the cells wrap into an
    // unreadable stack and the text paints outside the card. Past it, the wrapper scrolls --
    // a no-op wherever the card is already wider than this.
    minWidth: "620px",
  };

  const thStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "2px solid #000",
    fontWeight: "700",
    color: "#333",
    backgroundColor: "#f8f9fa",
  };

  const tdStyle = {
    textAlign: "center",
    padding: "10px 8px",
    borderBottom: "1px solid #e0e0e0",
  };

  const firstColumnStyle = {
    ...tdStyle,
    textAlign: "left",
    fontWeight: "600",
    backgroundColor: "#f8f9fa",
  };

  const formatCurrency = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return normalizedValue < 0 ? `-$${Math.abs(normalizedValue).toLocaleString()}` : `$${normalizedValue.toLocaleString()}`;
  };

  const formatPercent = (value) => {
    // Fix negative zero display
    const normalizedValue = value === 0 ? 0 : value;
    return `${normalizedValue.toFixed(2)}%`;
  };

  const formatBreakEvenDownPayment = (breakEvenPct, propertyPrice) => {
    if (breakEvenPct === null) return "N/A";
    // If break-even is less than 1%, show "Full Financing Possible"
    if (breakEvenPct < 1) {
      return (
        <>
          0%<br />
          Full Financing Possible
        </>
      );
    }
    const downPaymentAmount = Math.round((propertyPrice * breakEvenPct) / 100);
    return (
      <>
        {formatPercent(breakEvenPct)}<br />
        {formatCurrency(downPaymentAmount)}
      </>
    );
  };

  const getCashflowColor = (value) => {
    return value >= 0 ? "#28a745" : "#dc3545";
  };

  return (
    <div style={containerStyle}>
      <div style={titleStyle}>Rental Value Analysis</div>

      <div style={scrollWrapStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>Scenario</th>
            <th style={thStyle}>Low Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>High Rent<br/>P&I @ {analysis.piRate}%</th>
            <th style={thStyle}>Low Rent<br/>I/O @ {analysis.ioRate}%</th>
            <th style={thStyle}>High Rent<br/>I/O @ {analysis.ioRate}%</th>
            {tenantAnalysis && (
              <th style={{ ...thStyle, backgroundColor: "#ffebee", color: "#c62828" }}>Current Tenant<br/>I/O @ {analysis.ioRate}%</th>
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={firstColumnStyle}>Rent</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.rent)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.rent)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: 700, color: "#c62828" }}>{formatCurrency(tenantAnalysis.highIO.rent)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Mortgage</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.mortgage)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.mortgage)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee" }}>{formatCurrency(tenantAnalysis.highIO.mortgage)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Monthly Carry</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highPI.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.lowIO.carry)}</td>
            <td style={tdStyle}>{formatCurrency(analysis.highIO.carry)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee" }}>{formatCurrency(tenantAnalysis.highIO.carry)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Net Cashflow</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowPI.cashflow) }}>
              {formatCurrency(analysis.lowPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highPI.cashflow) }}>
              {formatCurrency(analysis.highPI.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.lowIO.cashflow) }}>
              {formatCurrency(analysis.lowIO.cashflow)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: getCashflowColor(analysis.highIO.cashflow) }}>
              {formatCurrency(analysis.highIO.cashflow)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "700", color: getCashflowColor(tenantAnalysis.highIO.cashflow) }}>{formatCurrency(tenantAnalysis.highIO.cashflow)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>
              Cap Rate (%)
              {property?.tax_is_estimated && (
                <span
                  title="Monthly tax is an offering-plan projection (sponsor estimate), typically well below the eventual assessed tax — this cap rate is optimistic."
                  style={{ marginLeft: 6, color: "#e65100", cursor: "help" }}
                >⚠</span>
              )}
            </td>
            <td style={tdStyle}>{formatPercent(analysis.lowPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highPI.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.lowIO.capRate)}</td>
            <td style={tdStyle}>{formatPercent(analysis.highIO.capRate)}</td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: 700, color: "#c62828" }}>{formatPercent(tenantAnalysis.highIO.capRate)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>IRR - Year 7</td>
            <td style={tdStyle}>-</td>
            <td style={tdStyle}>-</td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.lowIO.irrYear7)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "700", color: "#28a745" }}>
              {formatPercent(analysis.highIO.irrYear7)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "700", color: "#28a745" }}>{formatPercent(tenantAnalysis.highIO.irrYear7)}</td>}
          </tr>
          <tr>
            <td style={firstColumnStyle}>Break-Even Down Payment</td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highPI.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.lowIO.breakEvenDownPayment, propertyPrice)}
            </td>
            <td style={{ ...tdStyle, fontWeight: "600", color: "#0066cc" }}>
              {formatBreakEvenDownPayment(analysis.highIO.breakEvenDownPayment, propertyPrice)}
            </td>
            {tenantAnalysis && <td style={{ ...tdStyle, backgroundColor: "#ffebee", fontWeight: "600", color: "#0066cc" }}>{formatBreakEvenDownPayment(tenantAnalysis.highIO.breakEvenDownPayment, propertyPrice)}</td>}
          </tr>
        </tbody>
      </table>
      </div>

      {/* Which financing each column actually represents.
          The modal offers five type/term combinations; this table is pinned to two of them, so
          without saying so "P&I @ 6.25%" hides that it is the 30-year and not one of the ARMs —
          50bp apart on prod today. The extra columns vary the RENT, not the financing. */}
      <div style={footnoteStyle}>
        <span style={{ fontWeight: 600 }}>*</span>{" "}
        <strong>P&amp;I</strong> — {termLabel(analysis.piTerm)} at {analysis.piRate}%
        {analysis.piIsCustom && <em> (custom rate for this listing)</em>}, amortised over 30 years.{" "}
        <strong>I/O</strong> — {termLabel(analysis.ioTerm)} at {analysis.ioRate}%
        {analysis.ioIsCustom && <em> (custom rate for this listing)</em>}, interest only.{" "}
        Every P&amp;I option amortises over 30 years, so an ARM changes the rate, not the payoff
        period. Rates come from global Settings unless a custom rate is saved for this listing in
        IRR Assumptions.
      </div>
    </div>
  );
}

// The modal stores terms as "30yr Fixed" / "7/1 ARM" / "10/1 ARM"; spell the fixed one out.
function termLabel(term) {
  return term === "30yr Fixed" ? "30-year fixed" : term;
}

const footnoteStyle = {
  marginTop: "12px",
  fontSize: "11px",
  lineHeight: 1.5,
  color: "#666",
};
